import React, { useRef } from 'react';
import { Box, ButtonBase, Tooltip, Typography } from '@mui/material';
import {
  SearchOutlined,
  AutoAwesome,
  VisibilityOutlined,
  NotificationsOutlined,
  MenuOutlined,
} from '@mui/icons-material';
import { useShellTranslation } from './useShellTranslation';
import { shellTokens } from './shellTokens';
import { tokens } from '../../theme/tokens';
import { useShell } from './ShellContext';
import { useLocale } from '../../contexts/LocaleContext';
import { useAuth } from '../../contexts/AuthContext';
import { useImpersonation } from '../../contexts/ImpersonationContext';
import { formatUnreadCount } from './notificationsView';
import { NavModel } from './useNavModel';
import { shellSearchShortcut } from './shellShortcut';
import { UpdateChip } from '../appUpdate';
import { TrialChip } from './TrialChip';

const { color, size, radius, z } = shellTokens;

const REGION_FLAGS: Record<string, string> = { US: '🇺🇸', CA: '🇨🇦' };

/**
 * Width at which Ask Mozi can afford its label. Below it the label is dropped
 * so the right-hand cluster stays inside the header at 320px.
 */
const ASK_MOZI_LABEL_QUERY = '@media (min-width:400px)';

interface IconControlProps {
  label: string;
  onClick: (event: React.MouseEvent<HTMLButtonElement>) => void;
  children: React.ReactNode;
  buttonRef?: React.Ref<HTMLButtonElement>;
  badge?: number;
}

/**
 * Every control in the right-hand cluster except Ask Mozi is a fixed 34x34
 * icon. French copy runs ~50% longer than English; labelled controls here used
 * to push the bell past `main`'s clip edge and out of the page entirely.
 */
const IconControl: React.FC<IconControlProps> = ({
  label,
  onClick,
  children,
  buttonRef,
  badge = 0,
}) => (
  <Tooltip title={label}>
    <ButtonBase
      ref={buttonRef}
      onClick={onClick}
      aria-label={label}
      sx={{
        position: 'relative',
        width: 34,
        height: 34,
        flexShrink: 0,
        border: `1px solid ${color.line}`,
        background: color.surface,
        borderRadius: radius.control,
        color: color.bodyAlt,
        '&:hover': { background: color.lineSubtle, color: color.ink },
        '& svg': { fontSize: 17 },
      }}
    >
      {children}
      {badge > 0 && (
        <Box
          component="span"
          sx={{
            position: 'absolute',
            top: -5,
            right: -5,
            minWidth: 15,
            height: 15,
            px: '3px',
            borderRadius: '8px',
            background: color.danger,
            color: color.white,
            fontSize: '9.5px',
            fontWeight: 700,
            lineHeight: '15px',
            border: `1.5px solid ${color.white}`,
            boxSizing: 'border-box',
          }}
        >
          {formatUnreadCount(badge)}
        </Box>
      )}
    </ButtonBase>
  </Tooltip>
);

interface TopBarProps {
  nav: NavModel;
  isMobile: boolean;
  canViewAs: boolean;
  onAskMozi: () => void;
}

export const TopBar: React.FC<TopBarProps> = ({ nav, isMobile, canViewAs, onAskMozi }) => {
  const { t } = useShellTranslation();
  const { setOverlay, setMobileOpen, mobileOpen } = useShell();
  const { region, currency } = useLocale();
  const { unreadNotifications } = useAuth();
  // During View-as the only list the client can read is the admin's own, so
  // the badge would count someone else's queue under the impersonated banner.
  const { isImpersonating } = useImpersonation();
  const bellRef = useRef<HTMLButtonElement | null>(null);
  const regionRef = useRef<HTMLButtonElement | null>(null);

  // Breadcrumb: "Group / Page" for a row inside a group, or just the row's own
  // label for an ungrouped root row. Labels come from the same nav config the
  // sidebar renders, so the two can never disagree.
  const groupLabel = nav.activeGroup ? t(`shell.group.${nav.activeGroup.labelKey}`) : '';
  const pageLabel =
    nav.activeGroup && nav.activeLeaf ? t(`shell.item.${nav.activeLeaf.labelKey}`) : '';
  const section =
    !nav.activeGroup && nav.sectionLabelKey ? t(`shell.item.${nav.sectionLabelKey}`) : '';

  const anchorBelow = (element: HTMLElement | null) => {
    const rect = element?.getBoundingClientRect();
    return rect
      ? { top: rect.bottom + 8, right: window.innerWidth - rect.right }
      : { top: size.header + 8, right: 16 };
  };

  return (
    <Box
      component="header"
      sx={{
        height: size.header,
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'nowrap',
        gap: '10px',
        px: '16px',
        background: color.white,
        borderBottom: `1px solid ${color.line}`,
        zIndex: z.header,
      }}
    >
      {isMobile && (
        <ButtonBase
          onClick={() => setMobileOpen(!mobileOpen)}
          aria-label={t('shell.nav.openMenu')}
          sx={{ width: 34, height: 34, flexShrink: 0, borderRadius: radius.control, color: color.bodyAlt }}
        >
          <MenuOutlined sx={{ fontSize: 20 }} />
        </ButtonBase>
      )}

      {/* Breadcrumb. Below `sm` the group is dropped and only the page shows, so the right-hand cluster still fits at 320px. */}
      {groupLabel && pageLabel ? (
        <Box
          sx={{
            flex: '0 1 auto',
            minWidth: 0,
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
          }}
        >
          <Typography
            noWrap
            sx={{
              display: { xs: 'none', sm: 'block' },
              flex: '0 1 auto',
              minWidth: 0,
              fontSize: '13px',
              fontWeight: 500,
              color: color.muted,
            }}
          >
            {groupLabel}
          </Typography>
          <Typography
            component="span"
            aria-hidden
            sx={{
              display: { xs: 'none', sm: 'block' },
              flexShrink: 0,
              fontSize: '13px',
              color: color.faint,
            }}
          >
            /
          </Typography>
          <Typography
            noWrap
            sx={{
              flex: '0 1 auto',
              minWidth: 0,
              fontSize: '13px',
              fontWeight: 600,
              color: color.bodyAlt,
            }}
          >
            {pageLabel}
          </Typography>
        </Box>
      ) : (
        <Typography
          noWrap
          sx={{
            flex: '0 1 auto',
            minWidth: 0,
            fontSize: '13px',
            fontWeight: 600,
            color: color.bodyAlt,
          }}
        >
          {groupLabel || section}
        </Typography>
      )}

      <Box sx={{ flex: '1 1 auto', minWidth: '8px' }} />

      <IconControl
        label={t('shell.palette.tooltip', { shortcut: shellSearchShortcut() })}
        onClick={() => setOverlay({ kind: 'palette', mode: 'nav' })}
      >
        <SearchOutlined />
      </IconControl>

      {/* A store's trial and the days left (MOT-702); the admin and vendor shells have no trial. */}
      {nav.role === 'store' && <TrialChip />}

      <UpdateChip />

      <Tooltip title={t('shell.mozi.tooltip')}>
        <ButtonBase
          onClick={onAskMozi}
          aria-label={t('shell.mozi.label')}
          sx={{
            height: 34,
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            // At the narrowest widths the fixed controls alone consume the bar,
            // and this is the only one carrying non-shrinking text — so below
            // 400px it collapses to a 34px icon like its neighbours rather than
            // pushing the bell past main's clip edge.
            px: 0,
            width: 34,
            justifyContent: 'center',
            [ASK_MOZI_LABEL_QUERY]: { px: '12px', width: 'auto' },
            border: `1px solid ${color.accentBadge}`,
            background: color.accentTint,
            borderRadius: radius.control,
            fontSize: '13px',
            fontWeight: 600,
            color: color.accentDeep,
            whiteSpace: 'nowrap',
            '&:hover': { background: tokens.accent.tintStrong, borderColor: tokens.accent.border },
            '& svg': { fontSize: 15 },
          }}
        >
          <AutoAwesome />
          <Box
            component="span"
            sx={{ display: 'none', [ASK_MOZI_LABEL_QUERY]: { display: 'inline' } }}
          >
            {t('shell.mozi.label')}
          </Box>
        </ButtonBase>
      </Tooltip>

      {canViewAs && (
        <IconControl
          label={t('shell.viewAs.tooltip')}
          onClick={() => setOverlay({ kind: 'palette', mode: 'as' })}
        >
          <VisibilityOutlined />
        </IconControl>
      )}

      <Tooltip title={t('shell.region.tooltip')}>
        <ButtonBase
          id="shell-region-chip"
          ref={regionRef}
          aria-label={t('shell.region.tooltip')}
          onClick={() => setOverlay({ kind: 'region', at: anchorBelow(regionRef.current) })}
          sx={{
            height: 34,
            flexShrink: 0,
            display: { xs: 'none', sm: 'flex' },
            alignItems: 'center',
            gap: '6px',
            px: '10px',
            border: `1px solid ${color.line}`,
            background: color.surface,
            borderRadius: radius.control,
            '&:hover': { background: color.lineSubtle },
          }}
        >
          <Box component="span" sx={{ fontSize: '15px', lineHeight: 1 }}>
            {REGION_FLAGS[region] ?? '🌐'}
          </Box>
          <Typography sx={{ fontSize: '12.5px', fontWeight: 600, color: color.bodyAlt }}>
            {currency}
          </Typography>
        </ButtonBase>
      </Tooltip>

      <Box sx={{ width: '1px', height: 22, flexShrink: 0, background: color.line }} />

      <IconControl
        label={t('shell.notifications.tooltip')}
        buttonRef={bellRef}
        badge={isImpersonating ? 0 : unreadNotifications}
        onClick={() => setOverlay({ kind: 'notifications', at: anchorBelow(bellRef.current) })}
      >
        <NotificationsOutlined />
      </IconControl>
    </Box>
  );
};
